import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/lib/auth";
import { adminApi } from "@/lib/api";
import { STATUS_META, money } from "@/lib/order";
import OrderDrawer from "@/components/admin/OrderDrawer";

type View =
  | "dashboard" | "users" | "orders" | "quotations" | "payments"
  | "quotes" | "designs" | "artwork" | "pricing" | "colors" | "lockers";
const NAV: { key: View; label: string; icon: string }[] = [
  { key: "dashboard", label: "Dashboard", icon: "📊" },
  { key: "users", label: "Customers", icon: "👤" },
  { key: "orders", label: "Orders", icon: "📦" },
  { key: "quotations", label: "Quotations", icon: "💬" },
  { key: "payments", label: "Payments", icon: "💰" },
  { key: "quotes", label: "Enquiries", icon: "📝" },
  { key: "designs", label: "Designs", icon: "🎨" },
  { key: "artwork", label: "Artwork", icon: "🖼️" },
  { key: "pricing", label: "Pricing", icon: "🏷️" },
  { key: "colors", label: "Colours", icon: "🎨" },
  { key: "lockers", label: "Lockers", icon: "📍" },
];

function fmt(d?: string) {
  return d ? d.replace("T", " ").slice(0, 16) : "";
}

export default function AdminPage() {
  const [view, setView] = useState<View>("dashboard");
  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-[#0b111c] text-white">
        <div className="mx-auto max-w-7xl px-4 py-6 md:px-6 md:py-10 grid gap-6 md:gap-8 md:grid-cols-[220px_minmax(0,1fr)]">
          <aside className="min-w-0">
            <h2 className="text-xs font-bold uppercase tracking-wider text-amber-400 mb-3">Admin</h2>
            <nav className="space-y-1">
              {NAV.map((n) => (
                <button
                  key={n.key}
                  onClick={() => setView(n.key)}
                  className={`w-full text-left px-4 py-3 rounded-lg font-semibold transition ${
                    view === n.key ? "bg-[#11192b] text-amber-300" : "text-gray-400 hover:text-white"
                  }`}
                >
                  <span className="mr-2">{n.icon}</span>
                  {n.label}
                </button>
              ))}
            </nav>
          </aside>
          <section className="min-w-0">
            {view === "dashboard" && <Dashboard />}
            {view === "users" && <Users />}
            {view === "orders" && <Orders />}
            {view === "quotations" && <Orders quotationsOnly />}
            {view === "payments" && <Payments />}
            {view === "quotes" && <Quotes />}
            {view === "designs" && <Designs />}
            {view === "artwork" && <Artwork />}
            {view === "pricing" && <Pricing />}
            {view === "colors" && <Colors />}
            {view === "lockers" && <Lockers />}
          </section>
        </div>
      </main>
    </>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h1 className="text-3xl font-black mb-6">{title}</h1>
      {children}
    </div>
  );
}
function Th({ children }: { children?: React.ReactNode }) {
  return <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-gray-500">{children}</th>;
}
function Td({ children }: { children: React.ReactNode }) {
  return <td className="px-4 py-3 border-t border-sky-500/10">{children}</td>;
}
function Table({ children }: { children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-xl bg-[#11192b] border border-sky-500/10">
      <table className="w-full text-sm">{children}</table>
    </div>
  );
}

function Dashboard() {
  const [stats, setStats] = useState<any>(null);
  useEffect(() => {
    adminApi.stats().then((r) => setStats(r.stats)).catch(() => {});
  }, []);
  const items = [
    { k: "users", label: "Users", icon: "👤" },
    { k: "quotes", label: "Quotes", icon: "📝" },
    { k: "designs", label: "Designs", icon: "🎨" },
    { k: "artwork", label: "Artwork", icon: "🖼️" },
    { k: "orders", label: "Orders", icon: "📦" },
  ];
  return (
    <Panel title="Dashboard">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((i) => (
          <div key={i.k} className="rounded-xl bg-[#11192b] border border-sky-500/10 p-6">
            <div className="text-3xl">{i.icon}</div>
            <div className="mt-3 text-4xl font-black">{stats ? stats[i.k] ?? 0 : "—"}</div>
            <div className="text-gray-400">{i.label}</div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function Users() {
  const { startImpersonation, user: me } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[] | null>(null);
  const [busy, setBusy] = useState("");
  async function load() {
    try {
      setRows((await adminApi.users()).users);
    } catch {
      setRows([]);
    }
  }
  useEffect(() => {
    load();
  }, []);

  async function impersonate(id: string) {
    setBusy(id);
    try {
      const { token, user } = await adminApi.impersonate(id);
      startImpersonation(token, user);
      navigate("/account");
    } finally {
      setBusy("");
    }
  }
  async function toggleRole(u: any) {
    const role = u.role === "superadmin" ? "user" : "superadmin";
    await adminApi.setRole(u.id, role);
    load();
  }
  async function remove(u: any) {
    if (!window.confirm(`Delete ${u.email} and ALL their designs, artwork, orders and quotes? This cannot be undone.`))
      return;
    setBusy(u.id);
    try {
      await adminApi.deleteUser(u.id);
      load();
    } catch (e: any) {
      alert(e.message || "Delete failed");
    } finally {
      setBusy("");
    }
  }
  const FOUNDER = "johnkweber@gmail.com";

  if (!rows) return <Panel title="Users">Loading…</Panel>;
  return (
    <Panel title="Users">
      <Table>
        <thead>
          <tr>
            <Th>Email</Th>
            <Th>Name</Th>
            <Th>Role</Th>
            <Th>Designs</Th>
            <Th>Artwork</Th>
            <Th>Orders</Th>
            <Th>Quotes</Th>
            <Th>Actions</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((u) => (
            <tr key={u.id}>
              <Td>{u.email}</Td>
              <Td>{u.name || "—"}</Td>
              <Td>
                <span
                  className={`rounded-full px-2 py-1 text-xs font-bold ${
                    u.role === "superadmin" ? "bg-amber-500/20 text-amber-300" : "bg-sky-500/10 text-sky-300"
                  }`}
                >
                  {u.role}
                </span>
              </Td>
              <Td>{u.designs}</Td>
              <Td>{u.artwork}</Td>
              <Td>{u.orders}</Td>
              <Td>{u.quotes}</Td>
              <Td>
                <div className="flex gap-2">
                  <button
                    onClick={() => impersonate(u.id)}
                    disabled={busy === u.id}
                    className="rounded-lg bg-sky-500/20 text-sky-300 px-3 py-1 text-xs font-semibold hover:bg-sky-500/30 disabled:opacity-50"
                  >
                    Impersonate
                  </button>
                  <button
                    onClick={() => toggleRole(u)}
                    className="rounded-lg bg-amber-500/10 text-amber-300 px-3 py-1 text-xs font-semibold hover:bg-amber-500/20"
                  >
                    {u.role === "superadmin" ? "Make user" : "Make superadmin"}
                  </button>
                  {u.email !== FOUNDER && u.id !== me?.id && (
                    <button
                      onClick={() => remove(u)}
                      disabled={busy === u.id}
                      className="rounded-lg bg-red-500/15 text-red-300 px-3 py-1 text-xs font-semibold hover:bg-red-500/25 disabled:opacity-50"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Panel>
  );
}

function Quotes() {
  const [rows, setRows] = useState<any[] | null>(null);
  useEffect(() => {
    adminApi.quotes().then((r) => setRows(r.quotes)).catch(() => setRows([]));
  }, []);
  if (!rows) return <Panel title="Enquiries">Loading…</Panel>;
  return (
    <Panel title="Enquiries">
      <Table>
        <thead>
          <tr>
            <Th>When</Th>
            <Th>Name</Th>
            <Th>Contact</Th>
            <Th>User</Th>
            <Th>Message</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((q) => (
            <tr key={q.id}>
              <Td>{fmt(q.created_at)}</Td>
              <Td>{q.name}</Td>
              <Td>
                {q.phone} {q.email}
              </Td>
              <Td>{q.user_email || "guest"}</Td>
              <Td>
                <span className="line-clamp-2 max-w-md block">{q.message}</span>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Panel>
  );
}

function Orders({ quotationsOnly = false }: { quotationsOnly?: boolean }) {
  const [rows, setRows] = useState<any[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  async function load() {
    try {
      setRows((await adminApi.orders()).orders);
    } catch {
      setRows([]);
    }
  }
  useEffect(() => {
    load();
  }, []);
  const title = quotationsOnly ? "Quotations" : "Orders";
  if (!rows) return <Panel title={title}>Loading…</Panel>;
  const shown = quotationsOnly ? rows.filter((o) => o.payment_status !== "paid") : rows;
  if (!shown.length)
    return (
      <Panel title={title}>
        <p className="text-gray-400">{quotationsOnly ? "No open quotations." : "No orders yet."}</p>
      </Panel>
    );
  return (
    <Panel title={title}>
      <Table>
        <thead>
          <tr>
            <Th>Order</Th>
            <Th>Customer</Th>
            <Th>Items</Th>
            <Th>Quote</Th>
            <Th>Payment</Th>
            <Th>Status</Th>
            <Th>Files</Th>
            <Th></Th>
          </tr>
        </thead>
        <tbody>
          {shown.map((o) => {
            const meta = STATUS_META[o.status] ?? STATUS_META.draft;
            return (
              <tr key={o.id} className="cursor-pointer hover:bg-sky-500/5" onClick={() => setOpenId(o.id)}>
                <Td>
                  <span className="font-semibold text-white">{o.order_number || o.id.slice(0, 8)}</span>
                  <div className="text-xs text-gray-500">{fmt(o.created_at)}</div>
                </Td>
                <Td>{o.user_email || "—"}</Td>
                <Td>{o.item_count}</Td>
                <Td>{o.quote_amount != null ? money(o.quote_amount) : "—"}</Td>
                <Td>
                  <span
                    className={`rounded-full px-2 py-1 text-xs font-bold ${
                      o.payment_status === "paid" ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/15 text-rose-300"
                    }`}
                  >
                    {o.payment_status === "paid" ? "PAID" : "UNPAID"}
                  </span>
                </Td>
                <Td>
                  <span className={`rounded-full px-2 py-1 text-xs font-semibold ${meta.className}`}>{meta.label}</span>
                </Td>
                <Td>{o.file_count || 0}</Td>
                <Td>
                  <span className="text-sky-300 text-xs">Manage →</span>
                </Td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      {openId && <OrderDrawer orderId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </Panel>
  );
}

function Payments() {
  const [rows, setRows] = useState<any[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  async function load() {
    try {
      setRows((await adminApi.payments()).payments);
    } catch {
      setRows([]);
    }
  }
  useEffect(() => {
    load();
  }, []);
  if (!rows) return <Panel title="Payments">Loading…</Panel>;
  return (
    <Panel title="Payments">
      <Table>
        <thead>
          <tr>
            <Th>Order</Th>
            <Th>Customer</Th>
            <Th>Quote</Th>
            <Th>Paid</Th>
            <Th>Status</Th>
            <Th>Action</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.order_id}>
              <Td>
                <button onClick={() => setOpenId(p.order_id)} className="font-semibold text-sky-300 hover:underline">
                  {p.order_number || p.order_id.slice(0, 8)}
                </button>
              </Td>
              <Td>{p.user_email || "—"}</Td>
              <Td>{p.quote_amount != null ? money(p.quote_amount) : "—"}</Td>
              <Td>{money(p.paid_amount)}</Td>
              <Td>
                <span
                  className={`rounded-full px-2 py-1 text-xs font-bold ${
                    p.payment_status === "paid" ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/15 text-rose-300"
                  }`}
                >
                  {p.payment_status === "paid" ? "PAID" : "UNPAID"}
                </span>
              </Td>
              <Td>
                {p.payment_status === "paid" ? (
                  <button
                    onClick={async () => {
                      await adminApi.unpayOrder(p.order_id);
                      load();
                    }}
                    className="rounded-lg bg-rose-500/15 text-rose-300 px-3 py-1 text-xs font-semibold hover:bg-rose-500/25"
                  >
                    Unmark
                  </button>
                ) : (
                  <button
                    onClick={async () => {
                      await adminApi.payOrder(p.order_id);
                      load();
                    }}
                    className="rounded-lg bg-emerald-500/20 text-emerald-300 px-3 py-1 text-xs font-semibold hover:bg-emerald-500/30"
                  >
                    Mark paid
                  </button>
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
      {openId && <OrderDrawer orderId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </Panel>
  );
}

function Pricing() {
  const [p, setP] = useState<any | null>(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    adminApi.getPricing().then((r) => setP(r.pricing)).catch(() => setMsg("Could not load pricing"));
  }, []);
  if (!p) return <Panel title="Pricing">Loading…</Panel>;

  const num = (v: string) => Number(v) || 0;
  const set = (patch: any) => setP({ ...p, ...patch });
  const setSize = (i: number, patch: any) => {
    const printSizes = p.printSizes.map((s: any, idx: number) => (idx === i ? { ...s, ...patch } : s));
    set({ printSizes });
  };
  const addSize = () =>
    set({
      printSizes: [
        ...p.printSizes,
        { id: `tier${p.printSizes.length + 1}`, label: "New size", maxWidthCm: 10, maxHeightCm: 10, price: 0 },
      ],
    });
  const removeSize = (i: number) => set({ printSizes: p.printSizes.filter((_: any, idx: number) => idx !== i) });

  async function save() {
    setBusy(true);
    setMsg("");
    try {
      await adminApi.savePricing(p);
      setMsg("Saved ✓ — the studio and new orders now use these prices.");
    } catch (e: any) {
      setMsg(e.message || "Save failed");
    } finally {
      setBusy(false);
    }
  }

  const field = "rounded-lg bg-[#0b111c] border border-sky-500/20 px-3 py-2 text-white w-28";
  const box = "rounded-xl bg-[#11192b] border border-sky-500/10 p-6";

  return (
    <Panel title="Pricing">
      <div className="space-y-6 max-w-3xl">
        <div className={box}>
          <h3 className="font-bold mb-4">Base prices</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="text-sm text-gray-400">
              T-shirt garment price (print onto shirt)
              <input
                type="number"
                value={p.tshirt.garmentPrice}
                onChange={(e) => set({ tshirt: { garmentPrice: num(e.target.value) } })}
                className={`${field} mt-1 block`}
              />
            </label>
            <label className="text-sm text-gray-400">
              Gang sheet price (per 28×100cm sheet)
              <input
                type="number"
                value={p.gangsheet.price}
                onChange={(e) => set({ gangsheet: { price: num(e.target.value) } })}
                className={`${field} mt-1 block`}
              />
            </label>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            “Supply print only” omits the garment price — the customer just gets the transfer.
          </p>
        </div>

        <div className={box}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold">Print size tiers</h3>
            <button onClick={addSize} className="text-sky-300 text-sm hover:underline">
              + Add size
            </button>
          </div>
          <div className="space-y-3">
            {p.printSizes.map((s: any, i: number) => (
              <div key={i} className="flex flex-wrap items-end gap-3 border-b border-sky-500/10 pb-3">
                <label className="text-xs text-gray-400">
                  Label
                  <input value={s.label} onChange={(e) => setSize(i, { label: e.target.value })} className="rounded-lg bg-[#0b111c] border border-sky-500/20 px-3 py-2 text-white w-56 mt-1 block" />
                </label>
                <label className="text-xs text-gray-400">
                  Max W (cm)
                  <input type="number" value={s.maxWidthCm} onChange={(e) => setSize(i, { maxWidthCm: num(e.target.value) })} className={`${field} mt-1 block`} />
                </label>
                <label className="text-xs text-gray-400">
                  Max H (cm)
                  <input type="number" value={s.maxHeightCm} onChange={(e) => setSize(i, { maxHeightCm: num(e.target.value) })} className={`${field} mt-1 block`} />
                </label>
                <label className="text-xs text-gray-400">
                  Price (R)
                  <input type="number" value={s.price} onChange={(e) => setSize(i, { price: num(e.target.value) })} className={`${field} mt-1 block`} />
                </label>
                <button onClick={() => removeSize(i)} className="text-rose-300 text-sm hover:underline pb-2">
                  Remove
                </button>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-3">
            The studio picks the cheapest tier whose limits fit the placed artwork.
          </p>
        </div>

        <div className={box}>
          <h3 className="font-bold mb-4">Delivery fees</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="text-sm text-gray-400">
              Courier Guy (R)
              <input type="number" value={p.delivery.courier} onChange={(e) => set({ delivery: { ...p.delivery, courier: num(e.target.value) } })} className={`${field} mt-1 block`} />
            </label>
            <label className="text-sm text-gray-400">
              Pudo locker (R)
              <input type="number" value={p.delivery.pudo} onChange={(e) => set({ delivery: { ...p.delivery, pudo: num(e.target.value) } })} className={`${field} mt-1 block`} />
            </label>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button onClick={save} disabled={busy} className="rounded-lg bg-sky-500 px-6 py-3 font-bold text-white hover:bg-sky-400 disabled:opacity-60">
            {busy ? "Saving…" : "Save pricing"}
          </button>
          {msg && <span className="text-sm text-gray-300">{msg}</span>}
        </div>
      </div>
    </Panel>
  );
}

function Colors() {
  const [rows, setRows] = useState<any[] | null>(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    adminApi.getColors().then((r) => setRows(r.colors)).catch(() => setRows([]));
  }, []);
  if (!rows) return <Panel title="Garment Colours">Loading…</Panel>;

  const set = (i: number, patch: any) => setRows(rows.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const add = () => setRows([...rows, { name: "New", hex: "#888888" }]);
  const remove = (i: number) => setRows(rows.filter((_, idx) => idx !== i));

  async function save() {
    setBusy(true);
    setMsg("");
    try {
      const r = await adminApi.saveColors(rows ?? []);
      setRows(r.colors);
      setMsg("Saved ✓ — the studio swatches now use these colours.");
    } catch (e: any) {
      setMsg(e.message || "Save failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title="Garment Colours">
      <div className="rounded-xl bg-[#11192b] border border-sky-500/10 p-6 max-w-2xl">
        <p className="text-gray-400 text-sm mb-4">
          Colours the customer can choose for a t-shirt. The preview is tinted to the chosen colour;
          artwork is never recoloured.
        </p>
        <div className="space-y-3">
          {rows.map((c, i) => (
            <div key={i} className="flex items-center gap-3">
              <input
                type="color"
                value={/^#[0-9a-fA-F]{6}$/.test(c.hex) ? c.hex : "#888888"}
                onChange={(e) => set(i, { hex: e.target.value })}
                className="h-9 w-12 rounded bg-transparent border border-sky-500/20 cursor-pointer"
              />
              <input
                value={c.name}
                onChange={(e) => set(i, { name: e.target.value })}
                placeholder="Name"
                className="rounded-lg bg-[#0b111c] border border-sky-500/20 px-3 py-2 text-white w-48"
              />
              <input
                value={c.hex}
                onChange={(e) => set(i, { hex: e.target.value })}
                placeholder="#rrggbb"
                className="rounded-lg bg-[#0b111c] border border-sky-500/20 px-3 py-2 text-white w-32 font-mono"
              />
              <button onClick={() => remove(i)} className="text-rose-300 text-sm hover:underline">
                Remove
              </button>
            </div>
          ))}
        </div>
        <button onClick={add} className="mt-4 text-sky-300 text-sm hover:underline">
          + Add colour
        </button>
        <div className="mt-6 flex items-center gap-4">
          <button onClick={save} disabled={busy} className="rounded-lg bg-sky-500 px-6 py-3 font-bold text-white hover:bg-sky-400 disabled:opacity-60">
            {busy ? "Saving…" : "Save colours"}
          </button>
          {msg && <span className="text-sm text-gray-300">{msg}</span>}
        </div>
      </div>
    </Panel>
  );
}

function Lockers() {
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [token, setToken] = useState("");
  const [reveal, setReveal] = useState(false);
  const [status, setStatus] = useState<{
    configured: boolean;
    source: string;
    value: string | null;
    masked: string | null;
  } | null>(null);

  useEffect(() => {
    adminApi.getPudoToken().then(setStatus).catch(() => {});
  }, []);

  async function saveToken() {
    setBusy("token");
    setMsg("");
    try {
      await adminApi.savePudoToken(token);
      setToken("");
      setStatus(await adminApi.getPudoToken());
      setMsg("PUDO API token saved.");
    } catch (e: any) {
      setMsg(e.message || "Could not save token");
    } finally {
      setBusy("");
    }
  }
  async function sync() {
    setBusy("sync");
    setMsg("");
    try {
      const r = await adminApi.syncLockers();
      setMsg(`Synced ${r.count} lockers from the PUDO API.`);
    } catch (e: any) {
      setMsg(e.message || "Sync failed");
    } finally {
      setBusy("");
    }
  }

  const field = "w-full rounded-lg bg-[#0b111c] border border-sky-500/20 px-3 py-2 text-white";

  return (
    <Panel title="PUDO Lockers">
      <div className="space-y-6 max-w-xl">
        <div className="rounded-xl bg-[#11192b] border border-sky-500/10 p-6">
          <h3 className="font-bold mb-2">PUDO API token</h3>
          <p className="text-gray-400 text-sm mb-3">
            Used to sync the live locker list.{" "}
            {status && (
              <span className={status.configured ? "text-emerald-300" : "text-rose-300"}>
                {status.configured ? `Currently configured (${status.source}).` : "Not configured yet."}
              </span>
            )}
          </p>

          {status?.value && (
            <div className="mb-3 flex items-center gap-3 rounded-lg bg-[#0b111c] border border-sky-500/20 px-3 py-2">
              <code className="text-sm text-sky-300 break-all flex-1">
                {reveal ? status.value : status.masked}
              </code>
              <button
                type="button"
                onClick={() => setReveal((v) => !v)}
                className="text-xs font-semibold text-sky-400 hover:text-sky-300 shrink-0"
              >
                {reveal ? "Hide" : "Reveal"}
              </button>
              <button
                type="button"
                onClick={() => navigator.clipboard?.writeText(status.value!)}
                className="text-xs font-semibold text-gray-400 hover:text-white shrink-0"
              >
                Copy
              </button>
            </div>
          )}

          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder={status?.value ? "Paste a new token to replace it" : "Paste your PUDO API token"}
            className={field}
            autoComplete="off"
          />
          <div className="mt-3 flex items-center gap-3">
            <button
              onClick={saveToken}
              disabled={busy === "token" || !token.trim()}
              className="rounded-lg bg-sky-500 px-5 py-2.5 font-bold text-white hover:bg-sky-400 disabled:opacity-60"
            >
              {busy === "token" ? "Saving…" : "Save token"}
            </button>
            <span className="text-xs text-gray-500">Stored on the server; superadmins can reveal it above.</span>
          </div>
        </div>

        <div className="rounded-xl bg-[#11192b] border border-sky-500/10 p-6">
          <h3 className="font-bold mb-2">Sync lockers</h3>
          <p className="text-gray-400 text-sm">
            Pulls the full South Africa locker list from PUDO and updates the delivery picker.
          </p>
          <button
            onClick={sync}
            disabled={busy === "sync"}
            className="mt-4 rounded-lg bg-emerald-500 px-5 py-2.5 font-bold text-white hover:bg-emerald-400 disabled:opacity-60"
          >
            {busy === "sync" ? "Syncing…" : "Sync lockers now"}
          </button>
        </div>

        {msg && <p className="text-sm text-gray-300">{msg}</p>}
      </div>
    </Panel>
  );
}

function Designs() {
  const [rows, setRows] = useState<any[] | null>(null);
  const [preview, setPreview] = useState<any | null>(null);
  useEffect(() => {
    adminApi.designs().then((r) => setRows(r.designs)).catch(() => setRows([]));
  }, []);
  if (!rows) return <Panel title="All Designs">Loading…</Panel>;
  if (!rows.length) return <Panel title="All Designs"><p className="text-gray-400">No designs yet.</p></Panel>;
  return (
    <Panel title="All Designs">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {rows.map((d) => (
          <button
            key={d.id}
            onClick={() => setPreview(d)}
            className="text-left rounded-xl bg-[#11192b] border border-sky-500/10 p-4 hover:border-sky-400/40 transition"
          >
            <div className="aspect-square rounded-lg bg-[#0b111c] overflow-hidden flex items-center justify-center mb-3">
              {d.thumbnail ? (
                <img src={d.thumbnail} className="max-h-full max-w-full object-contain" />
              ) : (
                <span className="text-gray-600 text-4xl">🎨</span>
              )}
            </div>
            <p className="font-semibold truncate">{d.name}</p>
            <p className="text-xs text-gray-500">
              {d.kind === "gangsheet" ? "Gang Sheet" : d.product || "T-Shirt"} · {d.user_email}
            </p>
          </button>
        ))}
      </div>
      {preview && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-6" onClick={() => setPreview(null)}>
          <div className="max-w-2xl w-full rounded-2xl bg-[#0d1524] border border-sky-500/20 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold">{preview.name}</h3>
                <p className="text-xs text-gray-500">
                  {preview.kind === "gangsheet" ? "Gang Sheet" : preview.product || "T-Shirt"} · {preview.user_email}
                </p>
              </div>
              <button onClick={() => setPreview(null)} className="text-gray-400 hover:text-white">
                ✕
              </button>
            </div>
            <div className="rounded-lg bg-white p-4 flex items-center justify-center min-h-[300px]">
              {preview.thumbnail ? (
                <img src={preview.thumbnail} className="max-h-[60vh] max-w-full object-contain" />
              ) : (
                <span className="text-gray-400">No preview available</span>
              )}
            </div>
          </div>
        </div>
      )}
    </Panel>
  );
}

export function downloadDataUrl(dataUrl: string, filename: string) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename || "download";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// Artwork grouped per customer (a "directory" each), with hi-res download.
function Artwork() {
  const [rows, setRows] = useState<any[] | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  useEffect(() => {
    adminApi.artwork().then((r) => setRows(r.artwork)).catch(() => setRows([]));
  }, []);
  if (!rows) return <Panel title="Artwork by Customer">Loading…</Panel>;
  if (!rows.length) return <Panel title="Artwork by Customer"><p className="text-gray-400">No artwork yet.</p></Panel>;

  const groups: Record<string, any[]> = {};
  for (const a of rows) {
    const key = a.user_email || "unknown";
    (groups[key] ||= []).push(a);
  }
  const emails = Object.keys(groups).sort();

  return (
    <Panel title="Artwork by Customer">
      <div className="space-y-4">
        {emails.map((email) => {
          const list = groups[email];
          const isOpen = open[email] ?? emails.length <= 3;
          return (
            <div key={email} className="rounded-xl bg-[#11192b] border border-sky-500/10">
              <button
                onClick={() => setOpen((o) => ({ ...o, [email]: !isOpen }))}
                className="w-full flex items-center justify-between px-5 py-3 text-left"
              >
                <span className="font-semibold">
                  📁 {email} <span className="text-gray-500 text-sm">({list.length})</span>
                </span>
                <span className="text-gray-400">{isOpen ? "▾" : "▸"}</span>
              </button>
              {isOpen && (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 p-5 pt-0">
                  {list.map((a) => (
                    <div key={a.id} className="rounded-lg bg-[#0b111c] border border-sky-500/10 p-3">
                      <div className="aspect-square rounded-lg bg-black/40 overflow-hidden flex items-center justify-center mb-2">
                        {a.content_type?.startsWith("image/") && a.data ? (
                          <img src={a.data} className="max-h-full max-w-full object-contain" />
                        ) : (
                          <span className="text-gray-600 text-4xl">🖼️</span>
                        )}
                      </div>
                      <p className="font-semibold truncate text-sm">{a.filename}</p>
                      <p className="text-xs text-gray-500">{Math.round((a.size || 0) / 1024)} KB</p>
                      <button
                        onClick={() => a.data && downloadDataUrl(a.data, a.filename)}
                        disabled={!a.data}
                        className="mt-2 w-full rounded-lg bg-sky-500/20 text-sky-300 px-3 py-1.5 text-xs font-semibold hover:bg-sky-500/30 disabled:opacity-50"
                      >
                        ⬇ Download hi-res
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Panel>
  );
}
